import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { fetchSecurityCheckFamilyByCategory } from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { selectSecurityCheckFamiliesBySource } from '../../redux/securityCheckFamily/securityCheckFamilySelector'
import { formatNormLabel } from '../../utils/normLabels'

interface SecurityCheckFamily {
  _id?: string
  LabelSCF: string
}

interface SecurityCheckFamilyState {
  SecurityCheckFamilyResponse?: {
    securityCheckFamilies: SecurityCheckFamily[]
    total: number
    totalPages: number
  }
  dataSwift?: SecurityCheckFamily[]
  loading: boolean
  [key: string]: unknown
}

interface Norm {
  _id: string
  LabelSCF: string
}

interface NormListProps {
  selectedCategory: string
}

const NormList: React.FC<NormListProps> = ({ selectedCategory }) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const dataSource = useSelector(selectSecurityCheckFamiliesBySource)

  useEffect(() => {
    const action = fetchSecurityCheckFamilyByCategory({
      category: selectedCategory,
      page: 1,
      pageSize: 10,
      language: i18n.language.toLocaleUpperCase(),
    })
    if (action) dispatch(action)
  }, [dispatch, selectedCategory])

  const { norms, loading } = useSelector((state: RootState) => {
    let normsResult: Norm[] = []
    const securityState =
      state.securitycheckFamily as unknown as SecurityCheckFamilyState

    switch (selectedCategory) {
      case 'ISO':
      case 'ISO_13':
      case 'ISO_22':
      case 'NIST':
      case 'SOC': {
        const securityCheckFamilies =
          securityState.SecurityCheckFamilyResponse?.securityCheckFamilies ||
          []
        normsResult = securityCheckFamilies
          .filter(
            (item): item is SecurityCheckFamily & { _id: string } =>
              item._id !== undefined
          )
          .map((item) => ({ _id: item._id!, LabelSCF: item.LabelSCF }))
        break
      }
      case 'SWIFT': {
        const swiftData = securityState.dataSwift || []
        normsResult = swiftData
          .filter(
            (item): item is SecurityCheckFamily & { _id: string } =>
              item._id !== undefined
          )
          .map((item) => ({ _id: item._id!, LabelSCF: item.LabelSCF }))
        break
      }
      default: {
        const categoryData = dataSource?.securityCheckFamilies || []
        normsResult = categoryData
          .filter((item): item is any => !!item._id)
          .map((item) => ({
            _id: item._id as string,
            LabelSCF: item.LabelSCF,
          }))
        break
      }
    }

    return {
      norms: normsResult,
      loading: securityState.loading,
    }
  })

  const body = useMemo(() => {
    if (loading) {
      return (
        <div className="sn-preview-loading">
          {t('selectNormPage.loading')}
        </div>
      )
    }
    if (norms.length > 0) {
      return (
        <ul className="sn-preview-list">
          {norms.map((norm) => (
            <li key={norm._id} className="sn-preview-item">
              {norm.LabelSCF}
            </li>
          ))}
        </ul>
      )
    }
    return (
      <div className="sn-preview-empty">{t('selectNormPage.noNorms')}</div>
    )
  }, [norms, loading, t])

  return (
    <div className="sn-preview">
      <div className="sn-preview-head">
        <h4>
          {t('selectNormPage.normsTitle', {
            category: formatNormLabel(selectedCategory),
          })}
        </h4>
        {!loading && norms.length > 0 ? (
          <span className="sn-preview-count">
            {t('selectNormPage.normsCount', {
              count: norms.length,
              defaultValue: '{{count}} families',
            })}
          </span>
        ) : null}
      </div>
      {body}
    </div>
  )
}

export default NormList
